import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import Image from 'next/image';
import { Link } from '../Link/link';
import { TouchTarget } from '../Button/button';
import { Label } from '../../typography/label';
import { Title } from '../../typography/title';
import { Display } from '../../typography/display';

/**
 * Extracts the first initial from a full name.
 *
 * Supported cases:
 * - Input: "John Doe" → Output: "J"
 * - Input: "jane smith" → Output: "J"
 * - Input: "Mary-Jane Watson" → Output: "M"
 * - Input: "   Alice   " → Output: "A"
 * - Input: "!@#Bob123" → Output: "B"
 * - Input: "123User" → Output: "1"
 * - Input: "---" → Output: ""
 */
const getInitial = (fullName: string) => {
  const filteredTitle = fullName.replace(/[^a-zA-Z0-9]/g, '');
  const initial = filteredTitle.slice(0, 1).toUpperCase();

  return initial;
};

type AvatarProps = {
  fullName: string;
  src?: string | null;
  className?: string;
  size?: 's' | 'm' | 'l' | 'base';
};

export function Avatar({
  src = null,
  fullName,
  className,
  size = 'base',
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) {
  const initial = getInitial(fullName);

  return (
    <span
      data-slot="avatar"
      {...props}
      className={clsx(
        className,
        // Basic layout
        'inline-grid shrink-0 align-middle *:col-start-1 *:row-start-1',
        // Background
        src ? 'bg-primary-white' : 'bg-primary-black',
        'border border-neutral-300',
        // Border radius
        'overflow-hidden rounded-md',
        // Size
        size === 's' && 'h-[46px] w-[46px]',
        size === 'm' && 'size-[72px]',
        size === 'l' && 'size-[100px]',
        size === 'base' && 'size-44',
      )}
    >
      {src ? (
        <div className="flex size-full items-center justify-center">
          <Image
            className="aspect-square max-h-full max-w-full object-contain"
            src={src}
            alt={`Avatar for ${fullName}`}
            width={176}
            height={176}
            draggable={false}
          />
        </div>
      ) : (
        <div className="flex size-full items-center justify-center uppercase">
          {size === 's' && (
            <Label size="large" color="primary-white">
              {initial}
            </Label>
          )}
          {size === 'm' && (
            <Title size="tiny" as="p" color="primary-white">
              {initial}
            </Title>
          )}
          {size === 'l' && (
            <Title size="small" as="p" color="primary-white">
              {initial}
            </Title>
          )}
          {size === 'base' && (
            <Display size="tiny" as="p" color="primary-white">
              {initial}
            </Display>
          )}
        </div>
      )}
    </span>
  );
}

export const AvatarButton = forwardRef(
  (
    {
      src,
      fullName,
      className,
      size = 'base',
      ...props
    }: AvatarProps &
      (Omit<Headless.ButtonProps, 'as' | 'className'> | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>),
    ref: React.ForwardedRef<HTMLElement>,
  ) => {
    const classes = clsx(
      className,
      'rounded-[20%]',
      'relative inline-grid focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',
    );

    return 'href' in props ? (
      <Link
        {...(props as Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>)}
        className={classes}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        <TouchTarget>
          <Avatar src={src} fullName={fullName} size={size} />
        </TouchTarget>
      </Link>
    ) : (
      <Headless.Button
        {...(props as Omit<Headless.ButtonProps, 'as' | 'className'>)}
        className={classes}
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
      >
        <TouchTarget>
          <Avatar src={src} fullName={fullName} size={size} />
        </TouchTarget>
      </Headless.Button>
    );
  },
);
